'use client';

import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/_lib/client/redux/store';
import {
  loginSuccess,
  setRefreshStatus,
  logout,
  setError,
  setUnauthenticated,
  setLoading,
} from '@/_lib/client/redux/slices/authSlice';

const REFRESH_ENDPOINT = '/api/auth/refresh-token';

export function useSessionRefresher() {
  const dispatch = useDispatch<AppDispatch>();
  const status = useSelector((state: RootState) => state.auth.status);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    lastActivityRef.current ??= Date.now();

    const refresh = async () => {
      dispatch(setLoading());

      try {
        const response = await fetch(REFRESH_ENDPOINT, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 401) {
            dispatch(setUnauthenticated());
            dispatch(logout());
          } else {
            dispatch(setError(data.error || 'useSessionRefresher : Refresh failed'));
          }
        } else if (data.user) {
          dispatch(loginSuccess({ user: data.user }));
          dispatch(setRefreshStatus('idle'));
        } else {
          dispatch(setError('Invalid refresh response'));
          dispatch(logout());
        }
      } catch (err) {
        console.error('useSessionRefresher : Token refresh error:', err);
        dispatch(setError('useSessionRefresher : Network or server error'));
        dispatch(logout());
      } finally {
        setReady(true);
      }
    };

    refresh();

    intervalRef.current = setInterval(
      () => {
        const now = Date.now();
        if (lastActivityRef.current !== null && now - lastActivityRef.current < 5 * 60 * 1000) {
          refresh();
        }
      },
      10 * 60 * 1000
    );

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [dispatch]);

  return { status, ready };
}
